/**
 * Runtime sprite atlas: loads Tony's own hand-drawn tile sheet and hands out cells to draw. [P9-art]
 *
 * The mapping from donor sheet cell → canonical tile id + rotation is precomputed by
 * `tools/build-sprite-atlas.mjs` and checked in as `sprite-atlas-data.ts` — this module does no
 * matching of its own, it only loads the (copied-into-this-repo) sheet image and picks a cell.
 *
 * ── Why this never blocks tile rendering ─────────────────────────────────────────────────────────
 * The sheet is fetched once, eagerly, at module load. Until it resolves (or if it fails to load at
 * all), `trySpriteCell` returns `undefined` and `tile-texture.ts` falls back to the procedural
 * generator — never a blank tile. `spriteReady` lets a caller that rendered procedurally before load
 * completed redraw once art becomes available (see `tile-mesh.ts`).
 */

import { BASE_TILES } from '@carc/engine';
import { CELL_PX, SHEET_COLS, SHEET_URL, SPRITE_MAP, type SpriteCandidate } from './sprite-atlas-data.js';

export interface SpriteCell {
  readonly image: HTMLImageElement;
  readonly sx: number;
  readonly sy: number;
  readonly size: number;
  readonly rotation: 0 | 1 | 2 | 3;
}

let sheetImage: HTMLImageElement | null = null;

/** `Image`/`document` are undefined outside a browser (e.g. under vitest's `node` environment) — this
 * module must still be importable there, it just never has sprite art to offer. */
const inBrowser = typeof Image !== 'undefined' && typeof document !== 'undefined';

function loadSheet(): Promise<void> {
  if (!inBrowser) return Promise.resolve();
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      sheetImage = img;
      resolve();
    };
    img.onerror = () => resolve(); // stays on procedural fallback — never rejects/throws
    img.src = SHEET_URL;
  });
}

/** Resolves once the sheet has finished loading (successfully or not) — kicked off eagerly so art is
 * normally ready well before the first tile is placed. */
export const spriteReady: Promise<void> = loadSheet();

/** Deterministic pick among several candidate images for one canonical type, keyed off the tile
 * instance's own `drawIndex` — stable across re-renders and reloads, never per-frame random. */
function pickCandidate(candidates: readonly SpriteCandidate[], variationSeed: number): SpriteCandidate {
  const first = candidates[0] as SpriteCandidate;
  if (candidates.length === 1) return first;
  const hash = Math.imul(variationSeed ^ 0x9e3779b9, 2654435761) >>> 0;
  return candidates[hash % candidates.length] ?? first;
}

/** The sprite cell to draw for `defId`, or `undefined` if the sheet isn't loaded yet or no donor
 * artwork maps to this tile type — both are legitimate reasons to fall back to procedural art. */
export function trySpriteCell(defId: string, variationSeed: number): SpriteCell | undefined {
  if (!sheetImage) return undefined;
  const candidates = SPRITE_MAP[defId];
  if (!candidates || candidates.length === 0) return undefined;
  const chosen = pickCandidate(candidates, variationSeed);
  const col = chosen.cellIndex % SHEET_COLS;
  const row = Math.floor(chosen.cellIndex / SHEET_COLS);
  return { image: sheetImage, sx: col * CELL_PX, sy: row * CELL_PX, size: CELL_PX, rotation: chosen.rotation };
}

export interface CoverageEntry {
  readonly id: string;
  readonly copies: number;
  readonly imageCount: number;
}

/** Which of our 24 canonical types got real art and which fall back — derived from `BASE_TILES`
 * directly so it can never silently drift from the actual tile pack. */
export function coverageReport(): readonly CoverageEntry[] {
  return BASE_TILES.map((def) => ({
    id: def.id,
    copies: def.copies,
    imageCount: (SPRITE_MAP[def.id] ?? []).length,
  }));
}
